import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { queryKeys } from '../lib/queryClient';

const STALE_TIME = 1000 * 60 * 2; // 2 minutes

/**
 * Batched fetch for all dashboard metrics in a single async batch
 */
const fetchDashboardMetrics = async (userId) => {
  if (!userId) return null;

  const today = new Date()?.toISOString()?.split('T')?.[0];

  // Batch all queries in parallel
  const [workoutResult, nutritionResult, sleepResult, waterResult, profileResult] = await Promise.all([
    supabase?.from('workout_logs')?.select('steps, calories_burned, duration_minutes, workout_type')?.eq('user_id', userId)?.eq('workout_date', today),
    supabase?.from('nutrition_logs')?.select('calories, protein, carbs, fats, meal_type')?.eq('user_id', userId)?.eq('logged_date', today),
    supabase?.from('sleep_logs')?.select('duration_hours, quality_score, deep_sleep_hours, light_sleep_hours, rem_sleep_hours, bedtime, wake_time')?.eq('user_id', userId)?.order('sleep_date', { ascending: false })?.limit(1)?.maybeSingle(),
    supabase?.from('water_intake_logs')?.select('amount_ml')?.eq('user_id', userId)?.eq('logged_date', today),
    supabase?.from('user_profiles')?.select('daily_steps_goal, daily_calories_goal, daily_water_goal, sleep_goal')?.eq('id', userId)?.maybeSingle(),
  ]);

  if (workoutResult?.error) throw workoutResult?.error;
  if (nutritionResult?.error) throw nutritionResult?.error;
  if (sleepResult?.error) throw sleepResult?.error;
  // water_intake_logs may not exist yet — gracefully handle
  const waterData = waterResult?.error ? [] : (waterResult?.data || []);
  const profileData = profileResult?.error ? null : profileResult?.data;

  const workoutData = workoutResult?.data || [];
  const nutritionData = nutritionResult?.data || [];
  const sleepData = sleepResult?.data;

  // Aggregate metrics
  const totalSteps = workoutData?.reduce((sum, log) => sum + (log?.steps || 0), 0);
  const workoutCalories = workoutData?.reduce((sum, log) => sum + (log?.calories_burned || 0), 0);
  const totalActiveMins = workoutData?.reduce((sum, log) => sum + (log?.duration_minutes || 0), 0);
  // Estimate distance from steps (avg stride ~0.762m)
  const estimatedDistanceKm = parseFloat(((totalSteps * 0.762) / 1000)?.toFixed(2));

  const nutritionCalories = nutritionData?.reduce((sum, log) => sum + (log?.calories || 0), 0);
  const totalProtein = nutritionData?.reduce((sum, log) => sum + (log?.protein || 0), 0);
  const totalCarbs = nutritionData?.reduce((sum, log) => sum + (log?.carbs || 0), 0);
  const totalFats = nutritionData?.reduce((sum, log) => sum + (log?.fats || 0), 0);
  const mealsLogged = nutritionData?.length || 0;

  const sleepHours = sleepData ? parseFloat(sleepData?.duration_hours) || 0 : 0;
  const sleepQuality = sleepData ? (sleepData?.quality_score || 0) : 0;
  const deepSleepHours = sleepData ? parseFloat(sleepData?.deep_sleep_hours || 0) : 0;
  const remSleepHours = sleepData ? parseFloat(sleepData?.rem_sleep_hours || 0) : 0;

  // Water: sum all intake entries for today (in ml), convert to glasses (250ml each)
  const totalWaterMl = waterData?.reduce((sum, log) => sum + (log?.amount_ml || 0), 0);
  const totalWaterGlasses = parseFloat((totalWaterMl / 250)?.toFixed(1));

  // Goals from user profile (with sensible defaults)
  const stepsGoal = profileData ? (profileData?.daily_steps_goal || 10000) : 10000;
  const caloriesGoal = profileData ? (profileData?.daily_calories_goal || 2000) : 2000;
  const waterGoalGlasses = profileData ? (profileData?.daily_water_goal || 8) : 8;
  const sleepGoal = profileData ? (parseFloat(profileData?.sleep_goal) || 8) : 8;

  return {
    steps: { current: totalSteps, target: stepsGoal, trend: 0 },
    calories: { current: nutritionCalories, target: caloriesGoal, trend: 0, workoutCalories },
    water: { current: totalWaterGlasses, target: waterGoalGlasses, trend: 0, totalMl: totalWaterMl },
    sleep: { current: sleepHours, target: sleepGoal, trend: 0, qualityScore: sleepQuality, deepSleepHours, remSleepHours },
    // Rich today summary data
    todaySummary: {
      activeMins: totalActiveMins,
      distanceKm: estimatedDistanceKm,
      workoutCalories,
      nutritionCalories,
      protein: Math.round(totalProtein),
      carbs: Math.round(totalCarbs),
      fats: Math.round(totalFats),
      mealsLogged,
      sleepQuality,
      deepSleepHours,
      remSleepHours,
      waterMl: totalWaterMl,
    },
  };
};

/**
 * React Query hook for dashboard metrics with caching
 */
export const useDashboardMetricsQuery = (userId) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys?.dashboardMetrics(userId),
    queryFn: () => fetchDashboardMetrics(userId),
    enabled: !!userId,
    staleTime: STALE_TIME,
    placeholderData: {
      steps: { current: 0, target: 10000, trend: 0 },
      calories: { current: 0, target: 2000, trend: 0, workoutCalories: 0 },
      water: { current: 0, target: 8, trend: 0, totalMl: 0 },
      sleep: { current: 0, target: 8, trend: 0, qualityScore: 0, deepSleepHours: 0, remSleepHours: 0 },
      todaySummary: {
        activeMins: 0, distanceKm: 0, workoutCalories: 0, nutritionCalories: 0,
        protein: 0, carbs: 0, fats: 0, mealsLogged: 0,
        sleepQuality: 0, deepSleepHours: 0, remSleepHours: 0, waterMl: 0,
      },
    },
  });

  const invalidate = () =>
    queryClient?.invalidateQueries({ queryKey: queryKeys?.dashboardMetrics(userId) });

  return {
    metrics: query?.data,
    loading: query?.isLoading,
    error: query?.error ? query?.error?.message : null,
    refetch: invalidate,
  };
};
